/**
 * The session context and the gate that sits ABOVE the app shell.
 *
 * An unauthenticated user (when the gate is enforced) sees ONLY the login
 * screen — never the nav, the top bar, or any panel that would fire an API call
 * it is not allowed to make. When the gate is disabled (dev/CDP), `/api/auth/me`
 * reports a bypass session and the app renders as normal.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';

import { api, setOnUnauthorized } from '@/api/client';
import { LoginScreen } from '@/auth/LoginScreen';
import type { AuthStatus, Me } from '@/auth/types';

const AUTH_KEY = ['auth'] as const;
const qk = {
  status: [...AUTH_KEY, 'status'] as const,
  me: [...AUTH_KEY, 'me'] as const,
};

interface Session {
  status: AuthStatus | undefined;
  me: Me | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Re-check the session (after login/logout or a mid-session 401). */
  refresh: () => void;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error('useSession must be used within <AuthProvider>');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();

  const status = useQuery<AuthStatus>({
    queryKey: qk.status,
    queryFn: () => api.get<AuthStatus>('/auth/status'),
    staleTime: 30_000,
    retry: false,
  });

  const me = useQuery<Me>({
    queryKey: qk.me,
    // `/auth/me` answers 200 for a live session (or a bypass), 401 otherwise.
    // retry:false so a 401 lands as an error immediately (→ show login).
    queryFn: () => api.get<Me>('/auth/me'),
    staleTime: 15_000,
    retry: false,
  });

  useEffect(() => {
    // A 401 on any OTHER endpoint means the session lapsed mid-use: drop the
    // cached identity and re-check, which flips the gate back to the login
    // screen instead of leaving a wall of failed panels.
    setOnUnauthorized(() => {
      client.setQueryData<Me>(qk.me, { authenticated: false, email: null, amr: '', bypass: false });
      void client.invalidateQueries({ queryKey: AUTH_KEY });
    });
    return () => setOnUnauthorized(null);
  }, [client]);

  const value: Session = {
    status: status.data,
    me: me.data,
    isAuthenticated: me.data?.authenticated === true,
    // Only block on the first resolution; a background refetch must not flash
    // the splash over a working app.
    isLoading: status.isLoading || (me.isLoading && me.fetchStatus !== 'idle'),
    refresh: () => {
      void client.invalidateQueries({ queryKey: AUTH_KEY });
    },
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Shows the login screen until there is a valid session; then the app. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useSession();

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center bg-surface-0 text-lo">
        <div className="flex flex-col items-center gap-3">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-accent"
            aria-hidden
          />
          <span className="text-[12px]">Checking your session…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginScreen />;
  return <>{children}</>;
}
